import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { GlobalNavigation, TurnstileWidget, RegistrationSuccess as SharedRegistrationSuccess } from 'shared/components';
import { getTurnstileSiteKey } from 'shared/utils/turnstileConfig';
import { recoverRegistration } from '../api/recoverRegistration';
import '../styles/RecoverPage.css';
const CONTENT = {
    title: "Can't find your codes?",
    subtitle: 'Enter the email address you used to register to retrieve your codes.',
    emailLabel: 'Email',
    emailPlaceholder: 'Registration Email',
    backButton: 'Back',
    submitButton: 'Submit',
    registerLink: 'Register',
    errorNotFound: 'Ooops! This email is not registered. Select the link below to register and start the Hour of Finance Challenge.',
    errorInvalidEmail: 'Please enter a valid email address.',
    errorGeneric: 'An error occurred. Please try again later.',
    successTitle: 'Check your email!',
    successMessage: 'Your registration information has been sent to your email address.',
};
const RecoverPage = () => {
    const [email, setEmail] = useState('');
    const [turnstileToken, setTurnstileToken] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [turnstileResetKey, setTurnstileResetKey] = useState(0);
    const [recoveryData, setRecoveryData] = useState(null);
    const siteKey = getTurnstileSiteKey();
    const validateEmail = (value) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value.trim());
    };
    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        if (error) {
            setError(null);
        }
    };
    const handleBack = () => {
        // Navigate to main registration or previous page
        window.location.href = '/';
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) {
            setError('Email is required');
            return;
        }
        if (!validateEmail(email)) {
            setError(CONTENT.errorInvalidEmail);
            return;
        }
        if (!turnstileToken) {
            setError('Please complete the security challenge');
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            const data = await recoverRegistration(email, turnstileToken);
            setRecoveryData(data);
            setSuccess(true);
        }
        catch (err) {
            const error = err;
            setTurnstileResetKey(k => k + 1);
            if (error.status === 404) {
                // Internal users get a specific message from the backend; public users see the generic copy
                setError(error.message && error.message !== 'Failed to recover registration'
                    ? error.message
                    : CONTENT.errorNotFound);
            }
            else if (error.status === 400) {
                setError(CONTENT.errorInvalidEmail);
            }
            else {
                setError(error.message || CONTENT.errorGeneric);
            }
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleRegisterClick = () => {
        // Navigate to registration page
        window.location.href = 'https://hof2026.a.intuit.com/teacher';
    };
    // Show success page if email was sent
    if (success) {
        // Internal users (CS reps / Intuit employees) see codes directly
        if (recoveryData?.isInternalUser && recoveryData.joinCode) {
            const fullName = [recoveryData.firstName, recoveryData.lastName].filter(Boolean).join(' ');
            return (_jsxs(_Fragment, { children: [_jsx(GlobalNavigation, {}), _jsx("div", { className: "recover-page", children: _jsx(SharedRegistrationSuccess, { registrationType: recoveryData.registrationType || 'school', joinCode: recoveryData.joinCode, schoolName: recoveryData.schoolName, parentOrg: recoveryData.parentOrg, otherParentOrg: recoveryData.otherParentOrg, fullName: fullName, forceShowCode: true, showSurveyButton: false }) })] }));
        }
        return (_jsxs(_Fragment, { children: [_jsx(GlobalNavigation, {}), _jsx("div", { className: "recover-page", children: _jsx("div", { className: "recover-content", children: _jsxs("div", { className: "recover-success", children: [_jsx("h1", { className: "recover-success-title", children: CONTENT.successTitle }), _jsx("p", { className: "recover-success-message", children: CONTENT.successMessage }), _jsx("button", { type: "button", className: "recover-back-button", onClick: handleBack, children: CONTENT.backButton })] }) }) })] }));
    }
    return (_jsxs(_Fragment, { children: [_jsx(GlobalNavigation, {}), _jsx("div", { className: "recover-page", children: _jsxs("div", { className: "recover-content", children: [_jsxs("div", { className: "recover-header", children: [_jsx("h1", { className: "recover-title", children: CONTENT.title }), _jsx("p", { className: "recover-subtitle", children: CONTENT.subtitle })] }), _jsxs("form", { className: "recover-form", onSubmit: handleSubmit, children: [_jsxs("div", { className: `recover-field-container ${error ? 'has-error' : ''}`, children: [_jsx("label", { htmlFor: "email", className: "recover-label", children: CONTENT.emailLabel }), _jsx("input", { id: "email", type: "email", className: "recover-input", value: email, onChange: handleEmailChange, placeholder: CONTENT.emailPlaceholder, disabled: isLoading, maxLength: 100, "aria-invalid": !!error, "aria-describedby": error ? 'email-error' : undefined, autoComplete: "email" }), error && (_jsx("div", { id: "email-error", className: "recover-error-message", children: error }))] }), _jsx(TurnstileWidget, { siteKey: siteKey, onTokenChange: setTurnstileToken, className: "recover-turnstile", resetKey: turnstileResetKey }), _jsxs("div", { className: "recover-actions", children: [_jsxs("div", { className: "recover-button-container", children: [_jsx("button", { type: "button", className: "recover-back-button", onClick: handleBack, disabled: isLoading, children: CONTENT.backButton }), _jsx("button", { type: "submit", className: "recover-submit-button", disabled: isLoading || !email.trim(), children: isLoading ? 'Submitting...' : CONTENT.submitButton })] }), _jsx("a", { href: "#", onClick: (e) => { e.preventDefault(); handleRegisterClick(); }, className: "recover-register-link", children: CONTENT.registerLink })] })] })] }) })] }));
};
export default RecoverPage;
